/**
 * ActivityFeedItemContainer Component
 *
 * Container component for activity feed items. Handles:
 * - Parsing styled text with bold markers (**text**)
 * - Navigation to player page for scout report activity types
 * - Event handling and callback management
 * - Data transformation for presentation
 * @remarks
 * This component follows the Container/View pattern where:
 * - Container: Manages logic, state, hooks, and data transformation
 * - View: Pure presentation component receiving processed props
 * @example
 * ```tsx
 * <ActivityFeedItemContainer
 *   activityItem={item}
 *   bodyContent="Player added to watchlist"
 *   activityIcon={PlusIcon}
 *   enhancedTitle="John **Doe** was added"
 *   playerId="player-123"
 *   isSmallScreen={false}
 *   onPress={handlePress}
 *   isDark={true}
 * />
 * ```
 */

import { useRouter } from "expo-router";
import React, { useCallback, useMemo } from "react";

import ActivityFeedItemView from "./ActivityFeedItemView";

import { ActivityFeedFragment } from "@/generated/graphql";

/**
 * Props for ActivityFeedItemContainer
 */
interface ActivityFeedItemContainerProps {
  /** Activity item data from GraphQL */
  activityItem: ActivityFeedFragment;
  /** Processed body content text */
  bodyContent: string | null;
  /** Icon component to display for this activity type */
  activityIcon: React.ElementType;
  /** Theme-aware icon color class for this activity type */
  iconColor: string;
  /** Enhanced title with player name substitutions */
  enhancedTitle?: string;
  /** Player ID for navigation (scout report activity types) */
  playerId?: string | null;
  /** Whether screen width is below mobile breakpoint */
  isSmallScreen: boolean;
  /** Callback when activity item is pressed */
  onPress: (item: ActivityFeedFragment) => void;
  /** Whether dark theme is active */
  isDark: boolean;
}

/**
 * Styled text part with bold flag
 */
type StyledText = {
  text: string;
  bold: boolean;
};

/**
 * Parses text with **bold** markers into array of text parts
 * @param text - Text containing optional **bold** markers
 * @returns Array of text parts with bold flags
 * @example
 * parseStyledText("Hello **world**")
 * // Returns: [
 * //   { text: "Hello ", bold: false },
 * //   { text: "world", bold: true }
 * // ]
 */
const parseStyledText = (text: string): StyledText[] => {
  const parts = text.split(/(\*\*)/g);

  return parts.reduce(
    (acc, part) => {
      const { bold, items } = acc;

      if (part === "**") {
        return { bold: !bold, items };
      }

      if (!part) return acc;

      return {
        bold,
        items: [...items, { text: part, bold }],
      };
    },
    { bold: false, items: [] as StyledText[] }
  ).items;
};

/**
 * Container component for activity feed items
 * @param props - Component props
 * @param props.activityItem - Activity item data from GraphQL
 * @param props.bodyContent - Processed body content text
 * @param props.activityIcon - Icon component to display for this activity type
 * @param props.iconColor - Theme-aware icon color class for this activity type
 * @param props.enhancedTitle - Enhanced title with player name substitutions
 * @param props.playerId - Player ID for navigation (scout report activity types)
 * @param props.isSmallScreen - Whether screen width is below mobile breakpoint
 * @param props.onPress - Callback when activity item is pressed
 * @param props.isDark - Whether dark theme is active
 */
const ActivityFeedItemContainer = ({
  activityItem,
  bodyContent,
  activityIcon,
  iconColor,
  enhancedTitle,
  playerId,
  isSmallScreen,
  onPress,
  isDark,
}: ActivityFeedItemContainerProps) => {
  const router = useRouter();

  const handlePress = useCallback(() => {
    const activityCode = activityItem.activity.activityType.code;
    if (
      playerId &&
      (activityCode === "SCOUT_REPORT_CREATED" ||
        activityCode === "SCOUT_REPORT_UPDATED")
    ) {
      router.push(`/players/${playerId}`);
    } else {
      onPress(activityItem);
    }
  }, [playerId, activityItem, router, onPress]);

  const parsedParts = useMemo(
    () => parseStyledText(enhancedTitle || activityItem?.activity?.title || ""),
    [enhancedTitle, activityItem?.activity?.title]
  );

  return (
    <ActivityFeedItemView
      activityItem={activityItem}
      bodyContent={bodyContent}
      activityIcon={activityIcon}
      iconColor={iconColor}
      parsedParts={parsedParts}
      isSmallScreen={isSmallScreen}
      onPress={handlePress}
      isDark={isDark}
    />
  );
};

ActivityFeedItemContainer.displayName = "ActivityFeedItemContainer";

export default ActivityFeedItemContainer;
